import React from "react";
import { Check, ShieldAlert } from "lucide-react";
import useReveal from "../hooks/useReveal";
import useDocMeta from "../hooks/useDocMeta";
import ContactSection from "../components/ContactSection";
import FAQSection from "../components/FAQSection";
import HeroCover from "../components/HeroCover";
import { ESKETAMINE_PAGE as E, IMAGES, META, FAQ_SECTIONS } from "../mock";

const ESK_BULLETS = [
  "TGA-approved nasal spray (Spravato®)",
  "For adults with treatment-resistant depression",
  "Acts on the brain's glutamate system",
  "Self-administered under nursing supervision",
  "In-clinic treatment with medical monitoring",
];

export default function Esketamine() {
  useReveal();
  useDocMeta(META.esketamine || META.tms);
  return (
    <div>
      <HeroCover
        title="Esketamine (Spravato®) for Treatment-Resistant Depression"
        subtitle="A TGA-approved prescription nasal spray, used with an oral antidepressant, offering an evidence-based option for adults with treatment-resistant depression."
        bullets={ESK_BULLETS}
        badges={[]}
        image={IMAGES.loveseat}
        defaultHelp="Esketamine"
        showRibbon={true}
      />

      {/* How Esketamine Works — image left, text right */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal grid lg:grid-cols-2 gap-10 items-center" data-testid="esketamine-how-section">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[4/3]">
            <img src={IMAGES.treatmentRoom15856} alt="How esketamine works — AwareHub clinical care" className="h-full w-full object-cover" />
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-4">{E.howTitle}</h2>
            <p className="text-brand-900/75 leading-relaxed">{E.howBody}</p>
          </div>
        </div>
      </section>

      {/* Is Esketamine Right for You */}
      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-[2rem] bg-white border border-brand-100 p-8 sm:p-10 shadow-[0_18px_45px_-40px_rgba(31,56,53,0.5)] order-2 lg:order-1">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-3">{E.suitableTitle}</h2>
            <p className="text-brand-900/75 mb-5">{E.suitableIntro}</p>
            <ul className="space-y-3">
              {E.suitable.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-brand-900/80">
                  <Check className="h-5 w-5 mt-0.5 shrink-0 text-brand-500" strokeWidth={3} />{s}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-brand-900/70 italic">{E.suitableNote}</p>
          </div>
          <div className="order-1 lg:order-2 rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[4/3]">
            <img src={IMAGES.tealChairs} alt="Esketamine suitability consultation at AwareHub" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-4">{E.expectTitle}</h2>
          {E.expectIntro && (
            <p className="text-brand-900/75 leading-relaxed mb-6">{E.expectIntro}</p>
          )}
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {E.expect.map((c, i) => (
              <div key={i} className="rounded-2xl bg-sand-50 border border-brand-100 p-6 shadow-sm h-full">
                <span className="font-display text-4xl text-brand-300">0{i + 1}</span>
                <h3 className="mt-2 font-display text-xl text-brand-800">{c.h}</h3>
                <p className="mt-2 text-brand-900/70 leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Protocol & Schedule */}
      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[4/3]">
            <img src={IMAGES.relaxHeadphones} alt="Esketamine treatment protocol — client relaxing during monitored session at AwareHub" className="h-full w-full object-cover" data-testid="esketamine-protocol-photo" />
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-3">{E.protocolTitle}</h2>
            <p className="text-brand-900/75 mb-6">{E.protocolIntro}</p>
            <div className="space-y-4">
              {E.protocol.map((p, i) => (
                <div key={i} className="rounded-2xl bg-white border border-brand-200 p-6">
                  <h3 className="font-display text-xl text-brand-800">{p.h}</h3>
                  <p className="mt-1.5 text-brand-900/70">{p.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Accessing Care */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 reveal">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-brand-800 mb-4">{E.accessTitle}</h2>
          <div className="space-y-4">
            {E.accessBody.map((p, i) => (
              <p key={i} className="text-brand-900/75 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer + clinic banner */}
      <section className="py-12 sm:py-16 bg-sand-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 space-y-10">
          <div className="reveal rounded-[2rem] bg-brand-900 text-white p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <ShieldAlert className="h-6 w-6 text-brand-200" />
              <h2 className="font-display text-xl sm:text-2xl">{E.disclaimerTitle}</h2>
            </div>
            <p className="text-white/75 leading-relaxed text-sm">{E.disclaimer}</p>
          </div>
          <div className="reveal grid grid-cols-3 gap-3 sm:gap-5" data-testid="esketamine-photo-strip">
            {[IMAGES.greenWall, IMAGES.treatmentRoom15856, IMAGES.communityZone].map((src, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-[4/3] shadow-[0_24px_60px_-48px_rgba(31,56,53,0.5)]">
                <img src={src} alt="AwareHub clinic" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection background="bg-brand-50" sections={[FAQ_SECTIONS.esketamine]} pageTitle="Esketamine — Frequently Asked Questions" />

      <ContactSection />
    </div>
  );
}
